import React, { useEffect, useState, useContext } from "react";
import {
  Box, Typography, Button, Grid, Paper, Snackbar, Alert, MenuItem, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import useScopeAutoRefresh from "../../../hooks/useScopeAutoRefresh";
import { UserContext } from "../../../contexts/UserContext";
import LockIcon from "@mui/icons-material/Lock";

export default function AdmissionReviewPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [hasPermission, setHasPermission] = useState(false);
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    if (!user) {
      setHasPermission(false);
      return;
    }
    const role = user.role;
    setHasPermission(role === "SchoolAdmin" || role === "SuperAdmin");
  }, [user]);

  const loadAdmissions = async () => {
    if (!hasPermission) { setApplications([]); return; }
    const data = await fetchJson("/api/admissions");
    setApplications(Array.isArray(data) ? data : (data?.rows || data?.data || []));
  };

  useEffect(() => { loadAdmissions(); }, [hasPermission]);
  useScopeAutoRefresh([loadAdmissions]);

  const openDialog = (app) => {
    setSelected(app);
    setReviewStatus(app.status);
    setReviewNotes(app.notes || "");
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleReview = async () => {
    try {
      await fetchJson(`/api/admissions/${selected.id}/review`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: reviewStatus, notes: reviewNotes }),
      });
      setSnackbar({ open: true, msg: t("admissionReview.success") || "Application reviewed!", severity: "success" });
      setDialogOpen(false);
      // Refresh list
      loadAdmissions();
    } catch (e) {
      setSnackbar({ open: true, msg: t("admissionReview.error") || e.message, severity: "error" });
    }
  };

  if (!hasPermission) {
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", mt: 6, p: 3 }}>
        <Alert severity="error" icon={<LockIcon />}>
          {t("accessDenied.schoolAdminOnly") ||
            "Access denied. This page is restricted to School Administrators. Please contact your administrator if you believe this is a mistake."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", mt: 6, p: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        {t('admissionReviewPage.title') || 'Admission Review'}
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {applications.map((app) => (
            <Grid item xs={12} md={6} key={app.id}>
              <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="h6">{app.firstName} {app.lastName}</Typography>
                <Typography variant="body2">{app.email} | {app.phone}</Typography>
                <Typography variant="body2">{app.dob} | {app.gender}</Typography>
                <Typography variant="body2">{app.address}</Typography>
                <Box sx={{ mt: 1 }}>
                  {app.AdmissionDocuments && app.AdmissionDocuments.map((doc) => (
                    <Chip key={doc.id} label={doc.fileName} component="a" href={doc.fileUrl} target="_blank" clickable sx={{ mr: 1, mb: 1 }} />
                  ))}
                </Box>
                <Chip label={t(`admissionReview.status.${app.status}`) || app.status} color={app.status === "approved" ? "success" : app.status === "rejected" ? "error" : "warning"} sx={{ mt: 1 }} />
                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => openDialog(app)}>
                  {t("admissionReview.review") || "Review"}
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admissionReview.reviewTitle") || "Review Application"}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>{selected && `${selected.firstName} ${selected.lastName}`}</Typography>
          <TextField select label={t("admissionReview.statusLabel") || "Status"} value={reviewStatus} onChange={e => setReviewStatus(e.target.value)} fullWidth sx={{ mb: 2 }}>
            <MenuItem value="approved">{t("admissionReview.approve") || "Approve"}</MenuItem>
            <MenuItem value="rejected">{t("admissionReview.reject") || "Reject"}</MenuItem>
            <MenuItem value="pending">{t("admissionReview.pending") || "Pending"}</MenuItem>
          </TextField>
          <TextField label={t("admissionReview.notes") || "Notes"} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} fullWidth multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("admissionReview.cancel") || "Cancel"}</Button>
          <Button variant="contained" onClick={handleReview}>{t("admissionReview.save") || "Save"}</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
